import React from 'react';
import { useAppearance } from '../contexts/AppearanceContext';

/**
 * Order status pill.
 *
 * The two appearances encode status differently, on purpose:
 *
 * - FOREST keeps one semantic hue per status (warn / info / prog / ready /
 *   out / ok / neutral), with a glyph so state is never color alone.
 *
 * - FABLE follows the Organic style guide, which has four tag tones and encodes
 *   URGENCY rather than identity: terracotta = needs action, sage = in flight,
 *   solid sage = actively out, neutral = settled.
 *
 * That means four statuses (confirmed / in progress / ready x2) share a tone in
 * Fable and are told apart by their label, not their color. That is the
 * style guide's model, chosen deliberately over inventing hues the palette
 * does not have — but it IS a real change in how fast the table scans, and the
 * honest place to revisit if Orders feels slower to read.
 */

// Canonical labels — shared, since only the color differs by appearance.
// Wording follows the design's tags ("In progress", "Out for delivery"), which
// use sentence case rather than title case.
export const STATUS_LABELS = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  in_progress: 'In progress',
  ready_for_delivery: 'Ready',
  ready_for_pickup: 'Ready for pickup',
  out_for_delivery: 'Out for delivery',
  delivered: 'Delivered',
  picked_up: 'Picked up',
  cancelled: 'Cancelled',
  unpaid: 'Unpaid',
  paid: 'Paid',
  proposal_draft: 'Draft',
  proposal_sent: 'Sent',
  proposal_viewed: 'Viewed',
  proposal_responded: 'Responded',
};

// Forest — semantic status hues (design-handoff/forest-theme README §Phase 0.3).
// Glyphs (.tag-dot / .tag-check / .tag-arrow in index.css) draw in currentColor,
// so they pick up each tone's -fg color.
const WARN   = 'tag-dot bg-warn-bg text-warn-fg';
const INFO   = 'tag-dot bg-info-bg text-info-fg';
const PROG   = 'tag-dot bg-prog-bg text-prog-fg';
const READY  = 'tag-dot bg-ready-bg text-ready-fg';
const OUT    = 'tag-arrow bg-out-bg text-out-fg';        // the only solid tag
const OK     = 'tag-check bg-ok-bg text-ok-fg';
const NEUTRAL= 'bg-neutral-bg text-neutral-fg';
const FOREST_TONES = {
  pending: WARN, unpaid: WARN,
  confirmed: INFO,
  in_progress: PROG,
  ready_for_delivery: READY, ready_for_pickup: READY,
  out_for_delivery: OUT,
  delivered: OK, picked_up: OK, paid: OK,
  cancelled: NEUTRAL,
  // Event proposal statuses (backend/routes/proposals.js VALID_STATUSES).
  proposal_draft: NEUTRAL,
  proposal_sent: INFO, proposal_viewed: INFO,
  proposal_responded: OK,
  // Event pipeline statuses (events.status), prefixed so they never collide
  // with order statuses of the same name (in_progress, cancelled).
  event_new: INFO, event_responded: INFO, event_consultation: INFO, event_quoted: INFO,
  event_booked: READY,
  event_in_progress: PROG,
  event_completed: OK,
  event_passed: NEUTRAL, event_cancelled: NEUTRAL,
  // House-account invoices: sent = info, past due = danger.
  sent: INFO,
  past_due: 'tag-dot bg-danger-bg text-danger-fg',
};

// Fable — the style guide's four tones, as retuned in Sep 2026: each tag now
// carries a border one step darker than its fill, and settled is an outline
// rather than a fill. (The guide's accent-900 text is one step past this app's
// floral ramp; floral-900, its accent-800, is the closest step we have.)
//
// Glyphs follow the guide's rule that state is never color alone: a dot for
// needs action, an arrow for out, a check for settled. In flight carries none,
// which is itself distinct from the other three.
const NEEDS_ACTION = 'tag-dot border bg-floral-300 text-floral-900 border-floral-500'; // .tag-accent
const IN_FLIGHT = 'border bg-accent2-300 text-accent2-900 border-accent2-500'; // .tag-accent-2
const ACTIVE = 'tag-arrow border bg-accent2-700 text-accent2-100 border-accent2-700'; // .tag-solid-2, strongest
const SETTLED = 'tag-check border bg-transparent text-gray-600 border-gray-400'; // .tag-neutral

const FABLE_QUIET = 'border bg-transparent text-gray-500 border-gray-300';

const FABLE_TONES = {
  pending: NEEDS_ACTION,
  unpaid: NEEDS_ACTION,
  confirmed: IN_FLIGHT,
  in_progress: IN_FLIGHT,
  ready_for_delivery: IN_FLIGHT,
  ready_for_pickup: IN_FLIGHT,
  out_for_delivery: ACTIVE,
  delivered: SETTLED,
  picked_up: SETTLED,
  paid: SETTLED,
  cancelled: FABLE_QUIET,
  proposal_draft: FABLE_QUIET,
  proposal_sent: IN_FLIGHT,
  proposal_viewed: IN_FLIGHT,
  proposal_responded: SETTLED,
  // Events: no event colors of its own, so each takes the Fable tone its
  // Forest hue maps to for orders (info/ready/prog → in flight, ok → settled).
  event_new: IN_FLIGHT, event_responded: IN_FLIGHT, event_consultation: IN_FLIGHT, event_quoted: IN_FLIGHT,
  event_booked: IN_FLIGHT,
  event_in_progress: IN_FLIGHT,
  event_completed: SETTLED,
  event_passed: FABLE_QUIET, event_cancelled: FABLE_QUIET,
  sent: IN_FLIGHT,
  past_due: 'tag-dot border bg-danger-bg text-danger-fg border-danger',
};

const FALLBACK = { forest: NEUTRAL, fable: SETTLED };

/** Resolve just the color classes — for places that render their own element. */
export const useStatusToneClass = (status) => {
  const { appearance } = useAppearance();
  const map = appearance === 'fable' ? FABLE_TONES : FOREST_TONES;
  return map[status] || FALLBACK[appearance] || FALLBACK.forest;
};

// Tag geometry per appearance. Forest is a fixed 22px pill; Fable keeps its
// original padding-based shape.
const GEOMETRY = {
  forest: 'inline-flex items-center gap-1.5 h-[22px] px-2.5 rounded-full text-xs font-semibold whitespace-nowrap',
  fable: 'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs whitespace-nowrap',
};

const StatusTag = ({ status, label, count, className = '' }) => {
  const { appearance } = useAppearance();
  const tone = useStatusToneClass(status);
  const text = label || STATUS_LABELS[status] || status;
  const geometry = appearance === 'fable' ? GEOMETRY.fable : GEOMETRY.forest;

  return (
    <span className={`${geometry} ${tone} ${className}`}>
      {text}
      {count != null && <span className="font-bold">{count}</span>}
    </span>
  );
};

export default StatusTag;
